export const APP_ENVS = ["local", "preview", "production"];

export const PUBLIC_REQUIRED_ENV_KEYS = [
  "VITE_APP_ENV",
  "VITE_PUBLIC_SITE_URL",
  "VITE_PUBLIC_SUPPORT_EMAIL",
];

export const PUBLIC_INTEGRATION_ENV_KEYS = [
  "VITE_SUPABASE_URL",
  "VITE_SUPABASE_ANON_KEY",
];

export const PUBLIC_ENV_KEYS = [
  ...PUBLIC_REQUIRED_ENV_KEYS,
  ...PUBLIC_INTEGRATION_ENV_KEYS,
];

export const SERVER_ONLY_ENV_KEYS = [
  "SUPABASE_SERVICE_ROLE_KEY",
  "RESEND_API_KEY",
  "DATABASE_URL",
  "VERCEL_AUTH_TOKEN",
];

const LOCAL_PUBLIC_DEFAULTS = {
  VITE_APP_ENV: "local",
  VITE_PUBLIC_SITE_URL: "http://localhost:5173",
  VITE_PUBLIC_SUPPORT_EMAIL: "help@copticneighbor.com",
};

function isBlank(value) {
  return value == null || String(value).trim() === "";
}

export function normalizeAppEnv(value) {
  const appEnv = String(value || "").trim().toLowerCase();
  if (!APP_ENVS.includes(appEnv)) {
    throw new Error(
      `Unsupported app environment "${value}". Expected one of: ${APP_ENVS.join(", ")}.`
    );
  }
  return appEnv;
}

function withLocalDefaults(source) {
  const rawAppEnv = source.VITE_APP_ENV || LOCAL_PUBLIC_DEFAULTS.VITE_APP_ENV;
  const appEnv = normalizeAppEnv(rawAppEnv);
  if (appEnv !== "local") return { ...source, VITE_APP_ENV: appEnv };
  return { ...LOCAL_PUBLIC_DEFAULTS, ...source, VITE_APP_ENV: appEnv };
}

function validateUrl(key, value, appEnv) {
  let url;
  try {
    url = new URL(value);
  } catch {
    throw new Error(`${key} must be a valid absolute URL.`);
  }

  const localHttp = appEnv === "local" && url.protocol === "http:";
  if (url.protocol !== "https:" && !localHttp) {
    throw new Error(`${key} must use HTTPS outside local development.`);
  }
  return url.toString().replace(/\/$/, "");
}

function validateEmail(key, value) {
  const email = String(value || "").trim();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    throw new Error(`${key} must be a valid email address.`);
  }
  return email;
}

export function validatePublicEnv(source) {
  const env = withLocalDefaults(source || {});
  const leakedSecrets = SERVER_ONLY_ENV_KEYS.filter((key) => !isBlank(env[key]));
  if (leakedSecrets.length > 0) {
    throw new Error(
      `Server-only environment variables must not be exposed to the browser: ${leakedSecrets.join(", ")}.`
    );
  }

  const appEnv = normalizeAppEnv(env.VITE_APP_ENV);
  const requiredKeys = [...PUBLIC_REQUIRED_ENV_KEYS];
  if (appEnv !== "local") requiredKeys.push(...PUBLIC_INTEGRATION_ENV_KEYS);

  const missing = requiredKeys.filter((key) => isBlank(env[key]));
  if (missing.length > 0) {
    throw new Error(`Missing required public environment variables: ${missing.join(", ")}.`);
  }

  const hasSupabaseUrl = !isBlank(env.VITE_SUPABASE_URL);
  const hasSupabaseAnon = !isBlank(env.VITE_SUPABASE_ANON_KEY);
  if (hasSupabaseUrl !== hasSupabaseAnon) {
    throw new Error("VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY must be configured together.");
  }

  return {
    appEnv,
    publicSiteUrl: validateUrl("VITE_PUBLIC_SITE_URL", env.VITE_PUBLIC_SITE_URL, appEnv),
    publicSupportEmail: validateEmail("VITE_PUBLIC_SUPPORT_EMAIL", env.VITE_PUBLIC_SUPPORT_EMAIL),
    supabaseUrl: hasSupabaseUrl
      ? validateUrl("VITE_SUPABASE_URL", env.VITE_SUPABASE_URL, appEnv)
      : null,
    supabaseConfigured: hasSupabaseUrl && hasSupabaseAnon,
  };
}

export function getPublicRuntimeEnv(source = import.meta.env) {
  return validatePublicEnv(source || {});
}
